export const normalizeCampaignTemplateStatus = (template) => {
  const value = typeof template === 'string' ? template : template?.status;
  return String(value || '').trim().toUpperCase() || 'UNKNOWN';
};

export const isCampaignTemplateApproved = (template) => normalizeCampaignTemplateStatus(template) === 'APPROVED';

const templateWabaId = (template) => template?.businessId
  || template?.business_id
  || template?.whatsapp_business_account_id
  || template?.whatsapp_business_account?.id
  || template?.whatsapp_business_account;
const phoneWabaId = (phone) => phone?.businessAccountId
  || phone?.business_account_id
  || phone?.whatsapp_business_account_id
  || phone?.whatsapp_business_account?.id
  || phone?.whatsapp_business_account;

export const isCampaignTemplateEligible = (template, phone) => {
  const templateWaba = templateWabaId(template);
  const phoneWaba = phoneWabaId(phone);
  return isCampaignTemplateApproved(template) && !!templateWaba && !!phoneWaba && String(templateWaba) === String(phoneWaba);
};

export const campaignTemplateBlockReason = (template) => {
  if (isCampaignTemplateApproved(template)) return '';
  const status = normalizeCampaignTemplateStatus(template);
  const reason = template?.status_reason || template?.rejected_reason || template?.reason;
  return reason ? `Template is ${status}: ${reason}` : `Template is ${status}. Select an APPROVED template before continuing.`;
};

export const campaignTemplateEligibilityReason = (template, phone) => {
  const approvalReason = campaignTemplateBlockReason(template);
  if (approvalReason) return approvalReason;
  if (!templateWabaId(template) || !phoneWabaId(phone)) return 'Template business account could not be verified. Reselect the WhatsApp number and template.';
  if (!isCampaignTemplateEligible(template, phone)) return 'Template belongs to a different WhatsApp Business Account. Select a template for this number.';
  return '';
};

export const isCurrentCampaignDetail = (detail, {
  campaignId,
  expectedScopeKey,
  storedScopeKey,
  error = null,
}) => !error
  && !!detail?.id
  && String(detail.id) === String(campaignId)
  && expectedScopeKey === storedScopeKey;

export const whatsappTemplateIdFromStep = (step) => {
  const template = step?.whatsapp_template_id ?? step?.whatsapp_template;
  return template && typeof template === 'object' ? template.id : template;
};

export const nurtureWhatsappApproval = (messages, templates, phone) => {
  const enabledWhatsappSteps = (messages || []).filter(
    (step) => step?.is_enabled !== false && String(step?.channel || '').toUpperCase() === 'WHATSAPP'
  );
  if (enabledWhatsappSteps.length === 0) {
    return { allowed: true, reason: '' };
  }
  if (!phone?.id) {
    return { allowed: false, reason: 'Select the WhatsApp number used by this sequence before continuing.' };
  }

  const stepIsSendable = (step) => {
    const templateId = whatsappTemplateIdFromStep(step);
    const template = (templates || []).find((item) => String(item.id) === String(templateId));
    return !!templateId && !!template && !campaignTemplateEligibilityReason(template, phone);
  };
  if (enabledWhatsappSteps.some(stepIsSendable)) return { allowed: true, reason: '' };

  const blockedIndex = 0;
  const step = enabledWhatsappSteps[blockedIndex];
  if (step) {
    const templateId = whatsappTemplateIdFromStep(step);
    const template = (templates || []).find((item) => String(item.id) === String(templateId));
    const reason = templateId && template
      ? campaignTemplateEligibilityReason(template, phone)
      : 'has no verifiable template. Select an APPROVED template.';
    return { allowed: false, reason: `WhatsApp sequence step ${blockedIndex + 1}: ${reason}` };
  }
  return { allowed: false, reason: 'No sendable WhatsApp sequence step could be verified.' };
};

export const campaignActionApproval = (campaign) => {
  const channel = String(campaign?.channel || '').toUpperCase();
  const campaignType = String(campaign?.campaign_type || campaign?.campaignType || '').toUpperCase();
  if (channel !== 'WHATSAPP' && campaignType !== 'NURTURE') {
    return { allowed: true, reason: '' };
  }

  const decision = campaign?.whatsapp_template_eligibility;
  const isAuthoritativeDecision = decision
    && typeof decision === 'object'
    && typeof decision.eligible === 'boolean'
    && Object.prototype.hasOwnProperty.call(decision, 'code')
    && (decision.code === null || typeof decision.code === 'string')
    && typeof decision.message === 'string'
    && Array.isArray(decision.steps);
  if (!isAuthoritativeDecision) {
    return {
      allowed: false,
      reason: 'WhatsApp template eligibility could not be verified. Refresh the campaign before continuing.',
    };
  }

  if (decision.eligible) return { allowed: true, reason: '' };

  return {
    allowed: false,
    reason: String(decision.message || '').trim()
      || 'This campaign does not have an eligible WhatsApp template. Refresh it before continuing.',
  };
};

export const classifyCampaignSendEntry = (entry) => {
  const status = String(entry?.status || 'pending').trim().toLowerCase();
  const reason = String(entry?.error || entry?.reason || '').trim();
  const searchable = `${status} ${reason}`.toLowerCase();
  if (status === 'skipped' || /suppress|opt[ -]?out|unsubscrib|not open for communication/.test(searchable)) return { label: 'Suppressed', color: '#d97706', retryable: false, reason };
  if (/invalid.{0,20}(recipient|phone|number)|recipient.{0,20}invalid/.test(searchable)) return { label: 'Invalid recipient', color: '#ea580c', retryable: false, reason };
  if (status === 'failed') return { label: 'Failed', color: '#ef4444', retryable: true, reason };
  const fallback = { pending: ['Pending', '#94a3b8'], sent: ['Sent', '#0EA5E9'], skipped: ['Skipped', '#f59e0b'] }[status] || ['Pending', '#94a3b8'];
  return { label: fallback[0], color: fallback[1], retryable: false, reason };
};
